import { FC, ReactNode, useEffect } from 'react';

import { ScenarioHooks, useLocale } from '../../../../../hooks/index';
import { stakeholderTypeTranslation } from '../../../../../translations/stakeholder-translation.util';
import { ScenarioType } from '../../../../../types/scenario-modeling.types';
import { StakeholderType } from '../../../../../types/stakeholder.types';
import { toMathRound } from '../../../../../utils/to-math-round.util';
import { toNumber } from '../../../../../utils/to-number.util';
import { useScenarioCapTableStore } from '../../../cap-table.store';
import { CapTableTab, Column, StakeholdersViewBy } from '../liquidity.types';
import { TabProps, TotalRow } from '../table-components/index';
import { StakeholderTableItem } from './stakeholder-item';

type SelectedTabProps = {
  modifiedShareClassesIds?: string[];
  possibleShareClasses: string[];
} & Omit<TabProps, 'companyId'>;

export const stakeholdersCapTableViewBy: Record<
  'all' | StakeholdersViewBy,
  (props: SelectedTabProps) => ReactNode
> = {
  [StakeholdersViewBy.STAKEHOLDERS]: (props) => <StakeholdersItemTab {...props} />,
  [StakeholdersViewBy.TYPE]: (props) => <StakeholdersTypeTab {...props} />,
  all: (props) => <StakeholdersAllTab {...props} />,
};

export const StakeholdersTab: FC<TabProps> = ({
  visibleColumns,
  selectedViewBy,
  viewBy,
  scenarioId,
}) => {
  const { data: capTableShareClasses, isLoading: isLoadingShareClasses } =
    ScenarioHooks.useCapTableShareClasses<ScenarioType.LIQUIDITY>();

  const { messagesLocale } = useLocale();

  const { data: summarizeData, isLoading } =
    ScenarioHooks.useCapTableSummarize<ScenarioType.LIQUIDITY>();
  const { modifiedShareClasses } = ScenarioHooks.useModifiedShareClasses({ scenarioId });
  const modifiedShareClassesIds =
    modifiedShareClasses?.map(({ shareClass }) => shareClass.id) || [];

  const isCapTableLoading = Boolean(!summarizeData || isLoading || isLoadingShareClasses);
  const {
    diluted,
    dilutedPercentage,
    investment,
    issued,
    issuedPercentage,
    voting,
    commonStockProceeds,
    effectiveLiquidityProceeds,
    minLiquidityProceeds,
    participatingOnProceeds,
    proceedsWithoutLiquidity,
    votingPercentage,
    seniorityInfo,
  } = summarizeData || {};

  const defaultShareClass = capTableShareClasses?.find(({ isDefault }) => isDefault);
  const otherShareClass = capTableShareClasses?.filter(({ isDefault }) => !isDefault) || [];
  const possibleShareClasses = otherShareClass?.map(({ name }) => name) || [];

  const senioritiesFromSummarize = Object.values(seniorityInfo || {});

  const seniorities = senioritiesFromSummarize.length
    ? senioritiesFromSummarize
    : capTableShareClasses?.[0]?.seniorityColumns || [];

  return (
    <>
      {stakeholdersCapTableViewBy[selectedViewBy]({
        visibleColumns,
        selectedViewBy,
        viewBy,
        possibleShareClasses,
        scenarioId,
        modifiedShareClassesIds,
      })}

      <TotalRow
        fields={{
          [Column.COMMON_STOCK]: { value: toNumber(defaultShareClass?.issued), change: null },
          [Column.OTHER_SHARE_CLASSES]: {
            value: otherShareClass?.map(({ issued }) => issued),
            change: null,
          },
          [Column.TYPE]: {
            value: viewBy?.type ? messagesLocale['TOTAL'] : '',
            change: null,
          },
          [Column.NAME]: {
            change: null,
            value: messagesLocale['TOTAL'],
          },
          [Column.STAKEHOLDER_NAME]: {
            change: null,
            value: messagesLocale['TOTAL'],
          },
          [Column.ISSUED]: {
            change: null,
            value: toNumber(issuedPercentage) * 100,
          },
          [Column.DILUTED]: {
            value: toNumber(dilutedPercentage) * 100,
            change: null,
          },
          [Column.VOTING]: {
            value: toNumber(votingPercentage) * 100,
            change: null,
          },
          [Column.NO_ISSUED]: {
            change: null,
            value: toNumber(issued),
          },
          [Column.DILUTED_SHARES]: {
            change: null,
            value: toNumber(diluted),
          },

          [Column.VOTING_RIGHTS]: {
            change: null,
            value: toNumber(voting),
          },
          [Column.INVESTMENT]: {
            change: null,
            value: toNumber(investment),
          },
          [Column.AVERAGE_SHARE_PRICE]: {
            change: null,
            value: '-',
          },
          [Column.OTHER_SHARE_CLASSES_PROCEEDS]: otherShareClass.map(
            ({ capParticipating, investment, investmentDays, multiple }) => ({
              cap: capParticipating,
              investment,
              investmentTime: investmentDays,
              multiple: multiple,
            }),
          ),
          [Column.PROCEEDS_WITHOUT_LIQUIDITY_PROCEEDS]: {
            change: null,
            value: toNumber(proceedsWithoutLiquidity),
          },
          [Column.MIN_LIQUIDITY_PROCEEDS]: { change: null, value: toNumber(minLiquidityProceeds) },
          [Column.PARTICIPATING_ON_PROCEEDS]: {
            value: toMathRound(participatingOnProceeds, 0),
            change: null,
          },
          [Column.COMMON_STOCK_PROCEEDS]: { value: toNumber(commonStockProceeds), change: null },
          [Column.EFFECTIVE_LIQUIDITY_PROCEEDS]: {
            change: null,
            value: toNumber(effectiveLiquidityProceeds),
          },
          [Column.SENIORITY]: {
            value: seniorities.map(({ seniorityValue }) => seniorityValue),
            change: null,
          },

          [Column.ROI]: { change: null, value: '-' },
        }}
        selectedViewBy={selectedViewBy}
        tab={CapTableTab.BY_STAKEHOLDER}
        trClassName={isCapTableLoading && 'animate-pulse bg-gray-200/80'}
        viewBy={viewBy}
        visibleColumns={visibleColumns}
      />
    </>
  );
};

export const StakeholdersAllTab: FC<SelectedTabProps> = ({
  possibleShareClasses,
  selectedViewBy,
  visibleColumns,
  modifiedShareClassesIds = [],
}) => {
  const { messagesLocale } = useLocale();
  const {
    data: stakeholders,
    isLoading,
    isEmptySearch,
  } = ScenarioHooks.useCapTableStakeholderByAll<ScenarioType.LIQUIDITY>();

  const setEmptySearch = useScenarioCapTableStore(({ setEmptySearch }) => setEmptySearch);

  useEffect(() => {
    if (selectedViewBy !== 'all' || !stakeholders || isLoading) return;
    setEmptySearch(isEmptySearch);
  }, [selectedViewBy, stakeholders, isLoading, setEmptySearch, isEmptySearch]);

  return stakeholders?.map(
    (
      {
        stakeholders,
        type,
        diluted,
        dilutedPercentage,
        investment,
        issued,
        issuedPercentage,
        shareClasses,
        votingPercentage,
        voting,
        effectiveLiquidityProceeds,
        commonStockProceeds,
        minLiquidityProceeds,
        participatingOnProceeds,
        proceedsWithoutLiquidity,
        seniorityColumns,
      },
      i,
    ) => {
      const defaultShareClass = shareClasses.find(({ isDefault }) => isDefault);
      const otherShareClass = shareClasses.filter(
        ({ isDefault, name }) => !isDefault && possibleShareClasses.includes(name),
      );

      return (
        <StakeholderTableItem
          fields={{
            [Column.NAME]: {
              value: messagesLocale[stakeholderTypeTranslation[type as StakeholderType]],
              change: null,
            },
            [Column.STAKEHOLDER_NAME]: null,
            [Column.TYPE]: {
              change: null,
              value: messagesLocale[stakeholderTypeTranslation[type as StakeholderType]],
            },
            [Column.ISSUED]: {
              value: issuedPercentage * 100,
              change: null,
            },
            [Column.DILUTED]: {
              value: dilutedPercentage * 100,
              change: null,
            },
            [Column.VOTING]: {
              value: votingPercentage * 100,
              change: null,
            },
            [Column.NO_ISSUED]: {
              change: null,
              value: issued,
            },
            [Column.DILUTED_SHARES]: {
              change: null,
              value: diluted,
            },
            [Column.COMMON_STOCK]: {
              change: null,
              value: toNumber(defaultShareClass?.issued),
            },
            [Column.OTHER_SHARE_CLASSES]: {
              value: otherShareClass.map(({ issued }) => issued),
              change: null,
            },
            [Column.VOTING_RIGHTS]: {
              change: null,
              value: voting,
            },
            [Column.INVESTMENT]: {
              change: null,
              value: investment,
            },

            [Column.AVERAGE_SHARE_PRICE]: { value: '-', change: null },
            [Column.PROCEEDS_WITHOUT_LIQUIDITY_PROCEEDS]: {
              change: null,
              value: proceedsWithoutLiquidity,
            },
            [Column.MIN_LIQUIDITY_PROCEEDS]: { change: null, value: minLiquidityProceeds },
            [Column.PARTICIPATING_ON_PROCEEDS]: {
              change: null,
              value: toMathRound(participatingOnProceeds, 0),
            },
            [Column.COMMON_STOCK_PROCEEDS]: { value: commonStockProceeds, change: null },
            [Column.EFFECTIVE_LIQUIDITY_PROCEEDS]: {
              value: effectiveLiquidityProceeds,
              change: null,
            },
            [Column.OTHER_SHARE_CLASSES_PROCEEDS]: otherShareClass.map(
              ({ capParticipating, investmentDays, multiple, investment }) => ({
                cap: capParticipating,
                investment,
                investmentTime: investmentDays,
                multiple,
              }),
            ),
            [Column.SENIORITY]: {
              change: null,
              value: seniorityColumns?.map(({ seniorityValue }) => seniorityValue),
            },
            [Column.ROI]: { change: null, value: '-' },
          }}
          items={stakeholders}
          key={`${i}_${issued}${type}`}
          modifiedShareClassesIds={modifiedShareClassesIds}
          possibleShareClasses={possibleShareClasses}
          selectedViewBy={selectedViewBy}
          visibleColumns={visibleColumns}
        />
      );
    },
  );
};

export const StakeholdersTypeTab: FC<SelectedTabProps> = ({
  possibleShareClasses,
  visibleColumns,
  selectedViewBy,
  viewBy,
  scenarioId,
  modifiedShareClassesIds,
}) => {
  const { messagesLocale } = useLocale();
  const {
    data: stakeholderTypes,
    isLoading,
    isEmptySearch,
  } = ScenarioHooks.useCapTableStakeholderTypes<ScenarioType.LIQUIDITY>({
    scenarioId,
  });

  const setEmptySearch = useScenarioCapTableStore(({ setEmptySearch }) => setEmptySearch);

  useEffect(() => {
    if (selectedViewBy !== StakeholdersViewBy.TYPE || !stakeholderTypes || isLoading) return;

    setEmptySearch(isEmptySearch);
  }, [selectedViewBy, stakeholderTypes, isLoading, setEmptySearch, isEmptySearch]);

  return stakeholderTypes?.map(
    (
      {
        type,
        avgSharePrice,
        diluted,
        dilutedPercentage,
        investment,
        issued,
        issuedPercentage,
        shareClasses,
        votingPercentage,
        voting,
        commonStockProceeds,
        effectiveLiquidityProceeds,
        minLiquidityProceeds,
        participatingOnProceeds,
        proceedsWithoutLiquidity,
        seniorityColumns,
        roi,
      },
      i,
    ) => {
      const defaultShareClass = shareClasses.find(({ isDefault }) => isDefault);
      const otherShareClass = shareClasses.filter(
        ({ isDefault, name }) => !isDefault && possibleShareClasses.includes(name),
      );

      return (
        <StakeholderTableItem
          fields={{
            [Column.NAME]: null,
            [Column.STAKEHOLDER_NAME]: null,
            [Column.TYPE]: {
              change: null,
              value: messagesLocale[stakeholderTypeTranslation[type as StakeholderType]],
            },
            [Column.ISSUED]: {
              value: issuedPercentage * 100,
              change: null,
            },
            [Column.DILUTED]: {
              value: dilutedPercentage * 100,
              change: null,
            },
            [Column.VOTING]: {
              value: votingPercentage * 100,
              change: null,
            },

            [Column.NO_ISSUED]: {
              change: null,
              value: issued,
            },
            [Column.DILUTED_SHARES]: {
              change: null,
              value: diluted,
            },
            [Column.COMMON_STOCK]: {
              change: null,
              value: toNumber(defaultShareClass?.issued),
            },
            [Column.OTHER_SHARE_CLASSES]: {
              value: otherShareClass.map(({ issued }) => issued),
              change: null,
            },
            [Column.VOTING_RIGHTS]: {
              change: null,
              value: voting,
            },
            [Column.INVESTMENT]: {
              change: null,
              value: investment,
            },
            [Column.AVERAGE_SHARE_PRICE]: {
              change: null,
              value: avgSharePrice,
            },
            [Column.PROCEEDS_WITHOUT_LIQUIDITY_PROCEEDS]: {
              change: null,
              value: proceedsWithoutLiquidity,
            },
            [Column.MIN_LIQUIDITY_PROCEEDS]: { change: null, value: minLiquidityProceeds },
            [Column.PARTICIPATING_ON_PROCEEDS]: {
              change: null,
              value: toMathRound(participatingOnProceeds, 0),
            },
            [Column.COMMON_STOCK_PROCEEDS]: { value: commonStockProceeds, change: null },
            [Column.EFFECTIVE_LIQUIDITY_PROCEEDS]: {
              value: effectiveLiquidityProceeds,
              change: null,
            },
            [Column.OTHER_SHARE_CLASSES_PROCEEDS]: otherShareClass.map(
              ({ capParticipating, investmentDays, multiple, investment }) => ({
                cap: capParticipating,
                investment,
                investmentTime: investmentDays,
                multiple,
              }),
            ),
            [Column.SENIORITY]: {
              change: null,
              value: seniorityColumns?.map(({ seniorityValue }) => seniorityValue),
            },
            [Column.ROI]: { change: null, value: roi },
          }}
          key={`${i}_${issued}${type}`}
          modifiedShareClassesIds={modifiedShareClassesIds}
          possibleShareClasses={possibleShareClasses}
          selectedViewBy={selectedViewBy}
          viewBy={viewBy}
          visibleColumns={visibleColumns}
        />
      );
    },
  );
};

export const StakeholdersItemTab: FC<SelectedTabProps> = ({
  visibleColumns,
  selectedViewBy,
  viewBy,
  possibleShareClasses,
  modifiedShareClassesIds,
}) => {
  const { messagesLocale } = useLocale();
  const { data, isEmptySearch } = ScenarioHooks.useCapTableStakeholders<ScenarioType.LIQUIDITY>();

  const setEmptySearch = useScenarioCapTableStore(({ setEmptySearch }) => setEmptySearch);

  useEffect(() => {
    if (selectedViewBy !== StakeholdersViewBy.STAKEHOLDERS || !data) return;
    setEmptySearch(isEmptySearch);
  }, [data, isEmptySearch, selectedViewBy, setEmptySearch]);

  return data?.map(
    (
      {
        name,
        avgSharePrice,
        diluted,
        dilutedPercentage,
        investment,
        issued,
        issuedPercentage,
        shareClasses,
        votingPercentage,
        voting,
        stakeholder,
        commonStockProceeds,

        effectiveLiquidityProceeds,

        minLiquidityProceeds,
        participatingOnProceeds,
        proceedsWithoutLiquidity,
        seniorityColumns,
        roi,
      },
      i,
    ) => {
      const defaultShareClass = shareClasses.find(({ isDefault }) => isDefault);
      const otherShareClass = shareClasses.filter(
        ({ isDefault, name }) => !isDefault && possibleShareClasses.includes(name),
      );

      return (
        <StakeholderTableItem
          fields={{
            [Column.NAME]: null,
            [Column.STAKEHOLDER_NAME]: { change: null, value: name },
            [Column.TYPE]: {
              value:
                messagesLocale[stakeholderTypeTranslation[stakeholder?.type as StakeholderType]] ||
                'New Investor',
              change: null,
            },
            [Column.ISSUED]: {
              value: issuedPercentage * 100,
              change: null,
            },
            [Column.DILUTED]: {
              value: dilutedPercentage * 100,
              change: null,
            },
            [Column.VOTING]: {
              value: votingPercentage * 100,
              change: null,
            },

            [Column.NO_ISSUED]: {
              change: null,
              value: issued,
            },
            [Column.DILUTED_SHARES]: {
              change: null,
              value: diluted,
            },
            [Column.COMMON_STOCK]: {
              change: null,
              value: toNumber(defaultShareClass?.issued),
            },
            [Column.OTHER_SHARE_CLASSES]: {
              value: otherShareClass.map(({ issued }) => issued),
              change: null,
            },
            [Column.VOTING_RIGHTS]: {
              change: null,
              value: voting,
            },
            [Column.INVESTMENT]: {
              change: null,
              value: investment,
            },
            [Column.AVERAGE_SHARE_PRICE]: {
              change: null,
              value: avgSharePrice,
            },
            [Column.PROCEEDS_WITHOUT_LIQUIDITY_PROCEEDS]: {
              change: null,
              value: proceedsWithoutLiquidity,
            },
            [Column.MIN_LIQUIDITY_PROCEEDS]: { change: null, value: minLiquidityProceeds },
            [Column.PARTICIPATING_ON_PROCEEDS]: {
              change: null,
              value: toMathRound(participatingOnProceeds, 0),
            },
            [Column.COMMON_STOCK_PROCEEDS]: { value: commonStockProceeds, change: null },
            [Column.EFFECTIVE_LIQUIDITY_PROCEEDS]: {
              value: effectiveLiquidityProceeds,
              change: null,
            },
            [Column.OTHER_SHARE_CLASSES_PROCEEDS]: otherShareClass.map(
              ({ capParticipating, investmentDays, multiple, investment }) => ({
                cap: capParticipating,
                investment,
                investmentTime: investmentDays,
                multiple,
              }),
            ),
            [Column.SENIORITY]: {
              change: null,
              value: seniorityColumns?.map(({ seniorityValue }) => seniorityValue),
            },
            [Column.ROI]: { change: null, value: roi },
          }}
          key={`${i}_${issued}${name}`}
          modifiedShareClassesIds={modifiedShareClassesIds}
          possibleShareClasses={possibleShareClasses}
          selectedViewBy={selectedViewBy}
          viewBy={viewBy}
          visibleColumns={visibleColumns}
        />
      );
    },
  );
};
